import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconInfoCircle, IconSparkles } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { Currency, SimilarRequirementHit } from '@pta/types';
import {
  createRequirement,
  fetchSimilarRequirements,
  type GroupCurrencyConfig,
} from '../api/payment-requirements';
import { ApiError } from '../api/client';
import { toIsoDate } from '../util/format';

interface RequirementFormModalProps {
  opened: boolean;
  onClose: () => void;
  groupId: number;
  currencyConfig: GroupCurrencyConfig;
  currencies: Currency[];
  onSaved: (requirementId: number) => void;
}

const fullSpan = { gridColumn: '1 / -1' };

/**
 * Új payment requirement modal (FE).
 * Layout: mobilon 1 oszlop, desktopon (sm+) 2 oszlop.
 *
 * F2c — A `purpose` mező alá debounce-olt hasonlósági javaslat-doboz jelenik meg.
 */
export function RequirementFormModal({
  opened,
  onClose,
  groupId,
  currencyConfig,
  currencies,
  onSaved,
}: RequirementFormModalProps) {
  const { t } = useTranslation();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [purpose, setPurpose] = useState('');
  const [amount, setAmount] = useState<number | ''>('');
  const [currency, setCurrency] = useState<string>(currencyConfig.primaryCurrency);
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);

  const [suggestions, setSuggestions] = useState<SimilarRequirementHit[]>([]);
  const [suggestionsHidden, setSuggestionsHidden] = useState(false);

  useEffect(() => {
    if (opened) {
      setTitle('');
      setDescription('');
      setPurpose('');
      setAmount('');
      setCurrency(currencyConfig.primaryCurrency);
      setDueDate(null);
      setSuggestions([]);
      setSuggestionsHidden(false);
    }
  }, [opened, currencyConfig.primaryCurrency]);

  useEffect(() => {
    if (!opened) return;
    if (suggestionsHidden) return;
    const trimmed = purpose.trim();
    if (trimmed.length < 2) {
      setSuggestions([]);
      return;
    }
    const handle = setTimeout(() => {
      fetchSimilarRequirements(groupId, trimmed, 5)
        .then((hits) => setSuggestions(hits))
        .catch(() => setSuggestions([]));
    }, 350);
    return () => clearTimeout(handle);
  }, [purpose, opened, groupId, suggestionsHidden]);

  const handleApplySuggestion = (hit: SimilarRequirementHit) => {
    setPurpose(hit.purpose);
    setDescription(hit.description ?? '');
    setCurrency(hit.currency);
    setSuggestionsHidden(true);
    setSuggestions([]);
  };

  const currencyOptions = (() => {
    const relevant = new Set<string>();
    relevant.add(currencyConfig.primaryCurrency);
    if (currencyConfig.secondaryCurrency) {
      relevant.add(currencyConfig.secondaryCurrency);
    }
    const primaryAndSecondary = Array.from(relevant).map((code) => {
      const c = currencies.find((cc) => cc.code === code);
      return {
        value: code,
        label:
          code === currencyConfig.primaryCurrency
            ? `${code} — ${t('requirements.primary_hint', {
                ns: 'fe',
                defaultValue: 'elsődleges',
              })}`
            : c
            ? `${code}`
            : code,
      };
    });
    const others = currencies
      .filter((c) => !relevant.has(c.code))
      .map((c) => ({ value: c.code, label: c.code }));
    return [...primaryAndSecondary, ...others];
  })();

  const canSave =
    title.trim() !== '' &&
    purpose.trim() !== '' &&
    typeof amount === 'number' &&
    amount > 0 &&
    !!currency;

  const handleSave = async () => {
    if (!canSave || typeof amount !== 'number') return;
    setSaving(true);
    try {
      const amountStr = amount.toFixed(2).replace(/\.00$/, '');
      const result = await createRequirement(groupId, {
        title: title.trim(),
        description: description.trim() || null,
        purpose: purpose.trim(),
        amount: amountStr,
        currency,
        dueDate: dueDate ? toIsoDate(dueDate) : null,
        targets: null,
      });

      notifications.show({
        color: 'green',
        title: t('requirements.created_title', {
          ns: 'fe',
          defaultValue: 'Előírás létrehozva',
        }),
        message: t('requirements.created_msg', {
          ns: 'fe',
          defaultValue: '{{count}} diákra lett felvéve a target (draft státuszban).',
          count: result.targets.length,
        }),
      });
      onSaved(result.requirement.id);
    } catch (e) {
      notifications.show({
        color: 'red',
        message: e instanceof ApiError ? e.message : String(e),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      title={t('requirements.new', { ns: 'fe', defaultValue: 'Új előírás' })}
      size="lg"
    >
      <Stack>
        <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
          {t('requirements.new_info', {
            ns: 'fe',
            defaultValue:
              'Az új előírás draft státuszban jön létre. A target-ek automatikusan generálódnak a csoport összes aktív tagjára. Az előírás a "Publikálás" gomb megnyomásáig nem látszik a szülőknek.',
          })}
        </Alert>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" verticalSpacing="sm">
          <TextInput
            style={fullSpan}
            label={t('requirements.col_title', { ns: 'fe', defaultValue: 'Cím' })}
            description={t('requirements.title_hint', {
              ns: 'fe',
              defaultValue: 'rövid, beszédes megnevezés (pl. "Kirándulás 2026")',
            })}
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
            required
            maxLength={200}
          />

          <TextInput
            style={fullSpan}
            label={t('requirements.col_purpose', { ns: 'fe', defaultValue: 'Cél' })}
            description={t('requirements.purpose_hint', {
              ns: 'fe',
              defaultValue:
                'a cél rövid leírása (keresési egyezéshez; pl. "kirándulás", "ballagás")',
            })}
            value={purpose}
            onChange={(e) => {
              setPurpose(e.currentTarget.value);
              setSuggestionsHidden(false);
            }}
            required
            maxLength={200}
          />

          {suggestions.length > 0 && !suggestionsHidden && (
            <Card style={fullSpan} withBorder p="xs" bg="var(--mantine-color-teal-0)">
              <Group justify="space-between" mb="xs">
                <Group gap={6}>
                  <IconSparkles size={16} />
                  <Text size="sm" fw={500}>
                    {t('requirements.similar_title', {
                      ns: 'fe',
                      defaultValue: 'Hasonló korábbi előírások',
                    })}
                  </Text>
                </Group>
                <UnstyledButton onClick={() => setSuggestionsHidden(true)}>
                  <Text size="xs" c="dimmed">
                    {t('requirements.similar_hide', {
                      ns: 'fe',
                      defaultValue: 'elrejt',
                    })}
                  </Text>
                </UnstyledButton>
              </Group>
              <Stack gap={4}>
                {suggestions.map((hit) => (
                  <UnstyledButton
                    key={hit.id}
                    onClick={() => handleApplySuggestion(hit)}
                    style={{
                      padding: '6px 8px',
                      borderRadius: 4,
                      border: '1px solid var(--mantine-color-gray-3)',
                      background: 'white',
                    }}
                  >
                    <Group justify="space-between" wrap="nowrap">
                      <Stack gap={0} style={{ minWidth: 0 }}>
                        <Text size="sm" fw={500} truncate>
                          {hit.purpose}
                        </Text>
                        <Text size="xs" c="dimmed" truncate>
                          {hit.title}
                          {' · '}
                          {hit.institutionLabel
                            ? `${hit.institutionLabel} — ${hit.groupName}`
                            : hit.groupName}
                          {' · '}
                          {hit.occurredAt.slice(0, 10)}
                        </Text>
                      </Stack>
                      <Badge variant="light" size="sm" color="teal">
                        {Math.round(hit.similarity * 100)}%
                      </Badge>
                    </Group>
                  </UnstyledButton>
                ))}
              </Stack>
              <Text size="xs" c="dimmed" mt={6}>
                {t('requirements.similar_apply_hint', {
                  ns: 'fe',
                  defaultValue:
                    'Kattints egy találatra a cél, leírás és deviza átvételéhez.',
                })}
              </Text>
            </Card>
          )}

          <Textarea
            style={fullSpan}
            label={t('requirements.col_description', {
              ns: 'fe',
              defaultValue: 'Leírás (opcionális)',
            })}
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            autosize
            minRows={2}
            maxLength={5000}
          />

          <NumberInput
            label={t('requirements.col_amount', {
              ns: 'fe',
              defaultValue: 'Összeg',
            })}
            value={amount}
            onChange={(v) => setAmount(typeof v === 'number' ? v : '')}
            min={0}
            decimalScale={2}
            thousandSeparator=" "
            required
          />
          <Select
            label={t('requirements.col_currency', {
              ns: 'fe',
              defaultValue: 'Deviza',
            })}
            data={currencyOptions}
            value={currency}
            onChange={(v) => setCurrency(v ?? currencyConfig.primaryCurrency)}
            allowDeselect={false}
            required
          />

          <DateInput
            style={fullSpan}
            label={t('requirements.col_due_date', {
              ns: 'fe',
              defaultValue: 'Határidő (opcionális)',
            })}
            value={dueDate}
            onChange={(v) => setDueDate(v ? new Date(v) : null)}
            valueFormat="YYYY-MM-DD"
            clearable
          />
        </SimpleGrid>

        <Group justify="end">
          <Button variant="default" onClick={onClose} disabled={saving}>
            {t('common.cancel', { ns: 'common', defaultValue: 'Mégse' })}
          </Button>
          <Button onClick={handleSave} loading={saving} disabled={!canSave}>
            {t('requirements.create_btn', {
              ns: 'fe',
              defaultValue: 'Létrehozás (draft)',
            })}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
